/**
 * Removes credential values from URLs before anything records one.
 *
 * Lives in `shared` because both ends need it and a security fix applied to one
 * copy of a regex is how the other copy quietly stays broken.
 *
 * Two places put a credential in a URL, neither avoidable:
 *
 * - `/ws?token=…` — a WebSocket handshake cannot set an Authorization header,
 *   which `server/hooks.ts` calls out: a token in a query string is a credential
 *   written to every destination that records URLs.
 * - `#access_token=…` — Supabase confirmation and password-reset links return
 *   the session in the fragment, so it lands in the browser's address bar and in
 *   any navigation breadcrumb taken from it.
 *
 * It matters more than a cookie would. A Supabase JWT is base64, not encrypted,
 * so whoever reads the log also reads the account's email address out of the
 * payload, and can replay the token until it expires.
 */
/**
 * Any parameter whose name ends in `token`, plus `apikey`.
 *
 * Three things this has to handle, each learned the hard way:
 *
 * - **Suffix, not exact word.** `access_token` and `refresh_token` are what
 *   Supabase returns; matching only `token` passes them straight through.
 * - **`#` as a separator.** Confirmation and reset links carry the session in
 *   the fragment, so the whole reset flow lives after a `#`.
 * - **Start of string.** Sentry reports `query_string` on its own, as
 *   `token=…` with no leading `?`. A pattern that insists on a separator sees
 *   no match and forwards the credential intact — which is precisely the leak
 *   this function exists to stop, arriving through the side door.
 */
const CREDENTIAL_PARAM = /(^|[?&#])((?:[\w-]*token|apikey)=)[^&#]*/gi;
/**
 * The parameter name survives and only the value is replaced: a report should
 * still show that a credential was present, just not what it was.
 */
export const redactUrl = (value) => value.replace(CREDENTIAL_PARAM, "$1$2[redacted]");
/** The same rule for the parsed forms, where the key arrives on its own. */
export const isCredentialKey = (key) => /(?:[\w-]*token|apikey)$/i.test(key);
